import dayjs from 'dayjs';

import { REGIONS } from 'consts';

import { snapStore, snapStoreValues } from './snapStore';
import { ISnapStore, ISnapStoreActions } from './types';

import { getSnapMenuLineItem } from '../lib';

export const initSnapSuccessAction: ISnapStoreActions['initSnapSuccessAction'] = (data) => {
  const {
    locale,
    externalId,
    state: status,
    platform,
    imageUrl,
    lineItems,
    claimDetails,
    escalateDetails,
    collectorRegion,
    referenceCode,
    claimReferenceCode,
    canHaveOfferMatches,
    hasConflictedMatches,
    hasDirtyTransactions,
    hasFlippedDayAndYear,
    processingByOpsUserId,
    processingByOpsUserRole,
    processingByOpsUserEmail,
    processingByOpsUserIdStartedAt,
    isCompleteProcessingAllowed,
    isReprocessingAllowed,
    isFraudSuspicious,
    isCollectorFraudster,
    fraudSuspicionReason,
    isEscalated,
    isMarkedAsDone,
    nextSnapVisible,
    nextSnapAllowed,
    lineItemsCreationAllowed,
    requiresTroubleshooting,
    collectorId,
    duplicateOf,
    duplicates,
    submitTimestamp,
    total,
    subtotal,
    totalTax,
    merchant,
    receiptTimestamp,
    totalScore,
    totalOcrScore,
    totalTaxScore,
    totalTaxOcrScore,
    subtotalScore,
    subtotalOcrScore,
    receiptTimestampScore,
    receiptTimestampOcrScore,
    enabledBaseIssuanceRetailer,
  } = data;

  snapStore.setState({
    status,
    lineItems,
    claimDetails,
    escalateDetails,
    claimReferenceCode,
    processingByOpsUserId,
    processingByOpsUserRole,
    processingByOpsUserEmail,
    processingByOpsUserIdStartedAt,
    imgURL: imageUrl,
    refetchSnapData: false,
    fraudSuspicionReason,
    lineItemOptions: lineItems?.map(getSnapMenuLineItem),
    snapBooleanStatuses: {
      isEscalated,
      isMarkedAsDone,
      canHaveOfferMatches,
      hasConflictedMatches,
      hasDirtyTransactions,
      hasFlippedDayAndYear,
      isReprocessingAllowed,
      isFraudSuspicious,
      isCollectorFraudster,
      isCompleteProcessingAllowed,
      enabledBaseIssuanceRetailer,
      isProcessing: !!processingByOpsUserId,
      isNextSnapVisible: !!nextSnapVisible,
      isNextSnapAllowed: !!nextSnapAllowed,
      isRequiresTroubleshooting: requiresTroubleshooting,
      isLineItemsCreationAllowed: !!lineItemsCreationAllowed,
    },
    overviewFormValues: {
      locale: locale === 'fr_CA' ? 'FR' : 'EN',
      platform,
      duplicates,
      externalId,
      duplicateOf,
      collectorId,
      referenceCode,
      submittedDate: submitTimestamp ? dayjs(submitTimestamp) : null,
      collectorRegion: REGIONS.reduce<string>((acc, item) => {
        let region = acc;

        if (item.children && item.children.find((i) => i.value === collectorRegion)) {
          region = item.children.find((i) => i.value === collectorRegion)?.title as string;
        }

        return region;
      }, ''),
    },
    ocrFormValues: {
      total,
      subtotal,
      totalTax,
      retailerId: merchant,
      receiptTimestamp: receiptTimestamp ? dayjs(receiptTimestamp) : null,
    },
    ocrScores: {
      totalScore,
      totalOcrScore,
      totalTaxScore,
      totalTaxOcrScore,
      subtotalScore,
      subtotalOcrScore,
      receiptTimestampScore,
      receiptTimestampOcrScore,
    },
  });
};

export const initEligibleOfferOptionsSuccessAction: ISnapStoreActions['initEligibleOfferOptionsSuccessAction'] = (data) => {
  snapStore.setState({
    eligibleOfferOptions: data.map((item) => ({
      value: item.id,
      label: item.rewardMiles ? `${item.rewardMiles} miles - ${item.offerName}` : item.offerName,
    })),
  });
};

export const initEligibleProductsSuccessAction: ISnapStoreActions['initEligibleProductsSuccessAction'] = (eligibleProducts) => {
  snapStore.setState({
    refetchEligibleProductsData: false,
    eligibleProductIdentities: eligibleProducts.reduce<ISnapStore['eligibleProductIdentities']>((acc, item) => {
      acc.push({
        id: item.id,
        identities: item.identities.map((ind) => ({
          value: ind.id,
          label: `${ind.type} ${ind.value}`,
        })),
      });

      return acc;
    }, []),
    eligibleProductOptions: eligibleProducts.map((item) => ({
      value: item.id,
      label: item.productName,
    })),
  });
};

export const selectEligibleProductIdentityAction: ISnapStoreActions['selectEligibleProductIdentityAction'] = (id) => {
  snapStore.setState((state) => {
    const productIdentitiesOptions = state.eligibleProductIdentities.find((prod) => prod.id === id)?.identities;

    return {
      productIdentitiesOptions,
    };
  });
};

export const updateSnapBooleanStatusesSuccessAction: ISnapStoreActions['updateSnapBooleanStatusesSuccessAction'] = (data) => {
  snapStore.setState((state) => ({
    snapBooleanStatuses: {
      ...state.snapBooleanStatuses,
      ...data,
    },
  }));
};

export const updateSnapStatusSuccessAction: ISnapStoreActions['updateSnapStatusSuccessAction'] = (status) => {
  snapStore.setState((state) => ({
    status,
    refetchSnapData: state.status !== status,
  }));
};

export const updateSnapLineItemsSuccessAction: ISnapStoreActions['updateSnapLineItemsSuccessAction'] = ({ lineItems, lineItemOptions }) => {
  snapStore.setState({ lineItems, lineItemOptions });
};

export const setActiveTabAction: ISnapStoreActions['setActiveTabAction'] = (activeTabKey) => {
  snapStore.setState({ activeTabKey });
};

export const refetchSnapDetailsAction: ISnapStoreActions['refetchSnapDetailsAction'] = () => {
  snapStore.setState({ refetchSnapData: true });
};

export const refetchSnapEligibleProductsAction: ISnapStoreActions['refetchSnapEligibleProductsAction'] = () => {
  snapStore.setState({ refetchEligibleProductsData: true });
};

export const resetSnapAction: ISnapStoreActions['resetSnapAction'] = () => {
  snapStore.setState(snapStoreValues);
};
